import Page from '../../components/templates/Page'
import Welcome from '../../components/pages/real-estate/Welcome'
import Overview from '../../components/pages/real-estate/Overview'
import Promo from '../../components/pages/common/Promo'
import Professionals from '../../components/pages/common/Professionals'
import FAQs from '../../components/pages/common/FAQs'
import Contact from '../../components/pages/common/Contact'
import { useRef } from 'react'
import Properties from '../../components/pages/real-estate/Properties'
import Features from '../../components/pages/real-estate/Features'
import footerTranslation from '/components/general/Footer/footerTranslations'
import headerTranslation from '/components/general/Header/headerTranslation'
import contactTranslation from '/components/pages/common/Contact/contactTranslations'
import cache from 'memory-cache'
import generalEstateTranslations from '/components/pages/real-estate/generalEstateTranslations'
import homepageEstateTranslations from '../../components/pages/real-estate/homepageEstateTranslations'
import { useRouter } from 'next/router'
import { useResponsive } from '../../providers/ResponsiveContext'
import axios from '/components/configs/axios'

export const getStaticProps = async ({ locale }) => {
  try {
    // Fetch translations with caching
    let headerDataTranslation = cache.get('headerTranslations')
    if (!headerDataTranslation) {
      headerDataTranslation = await headerTranslation()
      cache.put('headerTranslations', headerDataTranslation)
    }

    let footerDataTranslation = cache.get('footerTranslations')
    if (!footerDataTranslation) {
      footerDataTranslation = await footerTranslation()
      cache.put('footerTranslations', footerDataTranslation)
    }

    let contactFormTranslationData = cache.get('contactFormTranslations')
    if (!contactFormTranslationData) {
      contactFormTranslationData = await contactTranslation()
      cache.put('contactFormTranslations', contactFormTranslationData)
    }

    let generalTranslation = cache.get('generalRealEstateTranslations')
    if (!generalTranslation) {
      generalTranslation = await generalEstateTranslations()
      cache.put('generalRealEstateTranslations', generalTranslation) // Fixed: added second parameter
    }

    let pageTranslation = cache.get('homepageEstateTranslations')
    if (!pageTranslation) {
      pageTranslation = await homepageEstateTranslations()
      cache.put('homepageEstateTranslations', pageTranslation) // Fixed: added second parameter
    }

    // Fetch page data in parallel with Promise.all
    const [pageDataResponse, propertiesResponse, exclusiveResponse] =
      await Promise.all([
        axios.get('real-estate/welcome'),
        axios.get('real-estate/properties'),
        axios.get('real-estate/exclusive')
      ])

    // Extract the data for the current locale
    const pageData = pageDataResponse.data[locale] || null
    const properties = propertiesResponse.data[locale] || []
    const exclusive = exclusiveResponse.data[locale] || null

    return {
      props: {
        headerTranslation: headerDataTranslation,
        footerTranslation: footerDataTranslation,
        contactTranslation: contactFormTranslationData,
        generalTranslation: generalTranslation,
        pageTranslation: pageTranslation,
        pageData,
        properties,
        exclusive
      },
      revalidate: 300
    }
  } catch (error) {
    console.error('Error fetching data for Real Estate page:', error)

    // Return minimal props if there's an error
    return {
      props: {
        headerTranslation: {},
        footerTranslation: {},
        contactTranslation: {},
        generalTranslation: {},
        pageTranslation: {},
        pageData: null,
        properties: [],
        exclusive: null
      },
      revalidate: 60 // Shorter revalidation time for error cases
    }
  }
}

const RealEstate = ({
  contactTranslation,
  headerTranslation,
  footerTranslation,
  generalTranslation,
  pageTranslation,
  pageData,
  properties,
  exclusive
}) => {
  const shadowRef = useRef(null)
  const router = useRouter()
  const scrollRef = useRef()
  const { isTablet, isSmallMobile } = useResponsive()

  return (
    <Page
      meta={{
        title: pageTranslation?.seo?.title?.[router.locale],
        description: pageTranslation?.seo?.description?.[router.locale]
      }}
      headerTranslation={headerTranslation}
      footerTranslation={footerTranslation}
      footer={false}
    >
      <Welcome
        translations={headerTranslation}
        totalProperties={properties?.length || 0}
        shadowRef={shadowRef}
        contentTranslations={pageTranslation?.welcome}
      />

      <Overview
        properties={properties || []}
        shadowRef={shadowRef}
        scrollRef={scrollRef}
        totalCards={isSmallMobile ? 1 : isTablet ? 2 : 3}
        buttonLabel={pageTranslation?.overview?.button?.[router.locale]}
        generalTranslation={generalTranslation}
      />

      {pageData?.promo && <Promo content={pageData.promo} />}

      <Features translations={pageTranslation?.features} />

      {exclusive && (
        <Properties
          translations={pageTranslation?.properties}
          generalTranslation={generalTranslation}
          scrollRef={scrollRef}
          properties={exclusive}
        />
      )}

      <Professionals
        title={pageTranslation?.professionals?.title?.[router.locale]}
        label={pageTranslation?.professionals?.label?.[router.locale]}
        pageData={pageData}
      />

      <FAQs
        title={pageTranslation?.faqs?.title?.[router.locale]}
        label={pageTranslation?.faqs?.label?.[router.locale]}
        pageData={pageData}
      />

      <Contact
        title={pageTranslation?.contact?.title?.[router.locale]}
        separator={false}
        staff={pageData?.person}
        variant={['white']}
        translations={contactTranslation}
      />
    </Page>
  )
}

export default RealEstate
